import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { Button } from '@/components/ui/button';
import {
  BookOpen, Star, Trophy, Sparkles, ChevronRight, Plus, Clock,
  Compass, Shield, Search, Wand2, Heart, Loader2, BookMarked, Award
} from 'lucide-react';
import { toast } from 'sonner';

const THEMES = [
  { id: 'adventure', label: 'Adventure', icon: Compass, color: 'from-amber-500 to-orange-500', bg: 'bg-amber-50', border: 'border-amber-200' },
  { id: 'mystery', label: 'Mystery', icon: Search, color: 'from-purple-500 to-indigo-500', bg: 'bg-purple-50', border: 'border-purple-200' },
  { id: 'fantasy', label: 'Fantasy', icon: Wand2, color: 'from-blue-500 to-cyan-500', bg: 'bg-blue-50', border: 'border-blue-200' },
  { id: 'friendship', label: 'Friendship', icon: Heart, color: 'from-pink-500 to-rose-500', bg: 'bg-pink-50', border: 'border-pink-200' },
  { id: 'school', label: 'School Life', icon: BookOpen, color: 'from-emerald-500 to-teal-500', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  { id: 'animals', label: 'Animals', icon: Shield, color: 'from-green-500 to-lime-500', bg: 'bg-green-50', border: 'border-green-200' },
  { id: 'resilience', label: 'Resilience', icon: Trophy, color: 'from-red-500 to-orange-500', bg: 'bg-red-50', border: 'border-red-200' },
  { id: 'real-life', label: 'Real Life', icon: Star, color: 'from-slate-500 to-gray-600', bg: 'bg-slate-50', border: 'border-slate-200' },
];

const SETTINGS = [
  { id: 'forest', label: 'Enchanted Forest' },
  { id: 'space', label: 'Outer Space' },
  { id: 'beach', label: 'Sunny Beach' },
  { id: 'school', label: 'School' },
  { id: 'city', label: 'Big City' },
  { id: 'castle', label: 'Magic Castle' },
  { id: 'underwater', label: 'Underwater World' },
  { id: 'mountain', label: 'Mountain Peak' },
];

const ReadingAdventure = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [progress, setProgress] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  // Story creation form
  const [selectedTheme, setSelectedTheme] = useState('');
  const [charName, setCharName] = useState('');
  const [setting, setSetting] = useState('');
  const [pet, setPet] = useState('');
  const [specialItem, setSpecialItem] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [storiesRes, progressRes, recsRes] = await Promise.all([
          api.get('/reading/stories'),
          api.get('/reading/progress'),
          api.get('/reading/recommendations'),
        ]);
        setStories(storiesRes.data.stories || []);
        setProgress(progressRes.data);
        setRecommendations(recsRes.data);
      } catch (err) {
        console.error('Failed to load reading data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const handleStartStory = async () => {
    if (!selectedTheme || !charName.trim() || !setting) {
      toast.error('Please choose a theme, name your character, and pick a setting!');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/reading/start-story', {
        theme: selectedTheme,
        character_name: charName.trim(),
        setting,
        pet: pet.trim() || null,
        special_item: specialItem.trim() || null,
      });
      toast.success('Your adventure begins!');
      navigate(`/student/reading/${res.data.story_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start story');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
          <span className="ml-3 text-slate-600">Loading your reading adventures...</span>
        </div>
      </StudentLayout>
    );
  }

  const inProgress = stories.filter(s => s.status === 'in_progress');
  const completed = stories.filter(s => s.status === 'completed');

  return (
    <StudentLayout>
      <div data-testid="reading-adventure-page" className="space-y-8">
        {/* Header */}
        <div className="student-card bg-gradient-to-br from-violet-600 to-purple-700 text-white">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <BookMarked className="w-9 h-9 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold">Reading Adventures</h1>
                <p className="text-purple-200 text-sm mt-1">Choose your story, make your choices, shape your adventure!</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-yellow-300" />
                  <span className="font-bold text-xl">{progress?.total_stars || 0}</span>
                </div>
                <p className="text-purple-200 text-xs">Stars</p>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1">
                  <Award className="w-5 h-5 text-yellow-300" />
                  <span className="font-bold text-xl">{progress?.badges?.length || 0}</span>
                </div>
                <p className="text-purple-200 text-xs">Badges</p>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1">
                  <BookOpen className="w-5 h-5 text-yellow-300" />
                  <span className="font-bold text-xl">{progress?.stories_completed || 0}</span>
                </div>
                <p className="text-purple-200 text-xs">Stories</p>
              </div>
            </div>
          </div>
          {/* Reading level indicator */}
          {progress?.reading_level && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-purple-200 text-xs">Reading Level:</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                progress.reading_level === 'hard' ? 'bg-red-400/30 text-red-100' :
                progress.reading_level === 'easy' ? 'bg-green-400/30 text-green-100' :
                'bg-blue-400/30 text-blue-100'
              }`}>
                {progress.reading_level === 'hard' ? 'Challenge Mode' : progress.reading_level === 'easy' ? 'Building Confidence' : 'Growing Reader'}
              </span>
            </div>
          )}
        </div>

        {/* Badges Display */}
        {progress?.badges?.length > 0 && (
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-800 mb-3">Your Badges</h2>
            <div className="flex flex-wrap gap-3">
              {progress.badges.map((badge, i) => (
                <div key={i} data-testid={`badge-${badge.id}`} className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <Award className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-semibold text-yellow-800">{badge.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Continue Reading */}
        {inProgress.length > 0 && (
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-800 mb-3">Continue Reading</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inProgress.map(story => (
                <Link key={story.story_id} to={`/student/reading/${story.story_id}`} data-testid={`continue-story-${story.story_id}`}>
                  <div className="student-card hover:shadow-lg transition-shadow cursor-pointer h-full">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-slate-800 text-sm">{story.title}</h3>
                        <span className="text-xs text-slate-500 capitalize">{story.theme}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full font-semibold">
                        Ch {story.current_chapter}/5
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-2">
                      <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${(story.current_chapter / 5) * 100}%` }} />
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Star className="w-3 h-3 text-yellow-500" /> {story.stars_earned || 0} stars</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Start New Story */}
        {!showCreate ? (
          <div className="text-center py-6">
            <Button
              data-testid="start-new-story-btn"
              onClick={() => setShowCreate(true)}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-8 py-3 text-base gap-2 rounded-xl"
              size="lg"
            >
              <Plus className="w-5 h-5" /> Start a New Adventure
            </Button>
          </div>
        ) : (
          <div data-testid="story-creator" className="student-card border-2 border-purple-200 space-y-6">
            <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" /> Create Your Story
            </h2>

            {/* Theme Selection */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Choose a Theme</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {THEMES.map(theme => {
                  const Icon = theme.icon;
                  return (
                    <button
                      key={theme.id}
                      data-testid={`theme-${theme.id}`}
                      onClick={() => setSelectedTheme(theme.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        selectedTheme === theme.id
                          ? `${theme.bg} ${theme.border} ring-2 ring-purple-400`
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-1 ${selectedTheme === theme.id ? 'text-purple-600' : 'text-slate-400'}`} />
                      <span className="text-sm font-semibold text-slate-700">{theme.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Character Name */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Name Your Character</label>
              <input
                data-testid="character-name-input"
                value={charName}
                onChange={e => setCharName(e.target.value)}
                placeholder="e.g. Luna, Max, Zara..."
                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none text-sm"
                maxLength={30}
              />
            </div>

            {/* Setting */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Choose Your Setting</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {SETTINGS.map(s => (
                  <button
                    key={s.id}
                    data-testid={`setting-${s.id}`}
                    onClick={() => setSetting(s.id)}
                    className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                      setting === s.id
                        ? 'bg-purple-50 border-purple-300 text-purple-700'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Optional: Pet & Special Item */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Pet (optional)</label>
                <input
                  data-testid="pet-input"
                  value={pet}
                  onChange={e => setPet(e.target.value)}
                  placeholder="e.g. a friendly dragon, clever cat..."
                  className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none text-sm"
                  maxLength={40}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Special Item (optional)</label>
                <input
                  data-testid="special-item-input"
                  value={specialItem}
                  onChange={e => setSpecialItem(e.target.value)}
                  placeholder="e.g. magic compass, golden key..."
                  className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none text-sm"
                  maxLength={40}
                />
              </div>
            </div>

            {/* Create Button */}
            <div className="flex gap-3">
              <Button
                data-testid="create-story-btn"
                onClick={handleStartStory}
                disabled={creating || !selectedTheme || !charName.trim() || !setting}
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white gap-2 flex-1"
              >
                {creating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Creating your adventure...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Begin Adventure!</>
                )}
              </Button>
              <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations?.recommendations?.length > 0 && !showCreate && (
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-800 mb-1">Recommended For You</h2>
            {recommendations.level_message && (
              <p className="text-sm text-purple-600 mb-3">{recommendations.level_message}</p>
            )}
            <div className="grid md:grid-cols-3 gap-4">
              {recommendations.recommendations.map((rec, i) => {
                const themeConfig = THEMES.find(t => t.id === rec.theme) || THEMES[0];
                const Icon = themeConfig.icon;
                return (
                  <button
                    key={i}
                    data-testid={`rec-${rec.theme}`}
                    onClick={() => { setShowCreate(true); setSelectedTheme(rec.theme); }}
                    className={`${themeConfig.bg} ${themeConfig.border} border-2 rounded-xl p-4 text-left hover:shadow-md transition-all`}
                  >
                    <Icon className="w-6 h-6 text-slate-600 mb-2" />
                    <h3 className="font-semibold text-slate-800 text-sm capitalize">{rec.theme}</h3>
                    <p className="text-xs text-slate-600 mt-1">{rec.reason}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Skills Progress */}
        {progress && progress.stories_completed > 0 && (
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-800 mb-3">Your Skills</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Reading', value: progress.skills?.reading || 0, color: 'bg-blue-500' },
                { label: 'Thinking', value: progress.skills?.thinking || 0, color: 'bg-purple-500' },
                { label: 'Confidence', value: progress.skills?.confidence || 0, color: 'bg-green-500' },
                { label: 'Vocabulary', value: progress.skills?.vocabulary || 0, color: 'bg-amber-500' },
              ].map((skill, i) => (
                <div key={i} className="student-card text-center">
                  <p className="text-2xl font-bold text-slate-800">{skill.value}</p>
                  <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                    <div className={`h-full ${skill.color} rounded-full`} style={{ width: `${Math.min(skill.value * 5, 100)}%` }} />
                  </div>
                  <p className="text-xs text-slate-500 mt-1 font-medium">{skill.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Stories */}
        {completed.length > 0 && (
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-800 mb-3">Completed Adventures</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {completed.map(story => (
                <Link key={story.story_id} to={`/student/reading/${story.story_id}`} data-testid={`completed-story-${story.story_id}`}>
                  <div className="student-card bg-emerald-50 border-emerald-200 hover:shadow-md transition-shadow cursor-pointer h-full">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-heading font-bold text-slate-800 text-sm">{story.title}</h3>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-semibold">Complete</span>
                    </div>
                    <p className="text-xs text-slate-500 capitalize mb-2">{story.theme} adventure</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Star className="w-3 h-3 text-yellow-500" /> {story.stars_earned || 0}</span>
                      <span>{story.total_words_read || 0} words read</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default ReadingAdventure;
